(() => {
  const selectors = {
    mediaThumb: '[data-product-media-thumb]',
    productRecommendations: '[data-product-recommendations]',
    variantOption: '[data-variant-option]',
    variantPicker: '[data-variant-picker]',
  };

  const recommendationLoadState = new WeakMap();
  const recommendationRetryTimers = new WeakMap();

  function queryAllIncludingRoot(root, selector) {
    const matches = [];
    if (root instanceof Element && root.matches(selector)) {
      matches.push(root);
    }
    matches.push(...root.querySelectorAll(selector));
    return matches;
  }

  function getProductRoot(node) {
    return node.closest('.md-pdp, .md-product-pdp');
  }

  function getProductMediaRoot(node) {
    return node.closest('.md-pdp__media, .md-product-pdp__gallery');
  }

  function getProductScope(node) {
    return (
      node.closest('.md-pdp__info, .md-product-pdp__buy-box') ||
      node.closest('section') ||
      document
    );
  }

  function getMainProductForm(picker) {
    return (
      getProductRoot(picker)?.querySelector(
        '.md-pdp__form, .md-product-pdp__form',
      ) || null
    );
  }

  function escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function getVariantData(picker) {
    const script = picker.querySelector('[data-product-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return [];
    }
  }

  function getSelectedOptions(picker) {
    return Array.from(picker.querySelectorAll('.md-pdp__variant-option')).map(
      (group) => {
        const checked = group.querySelector(
          `${selectors.variantOption}:checked`,
        );
        const select = group.querySelector('select[data-variant-option]');
        return checked?.value || select?.value || '';
      },
    );
  }

  function findSelectedVariant(picker) {
    const selectedOptions = getSelectedOptions(picker);
    return getVariantData(picker).find((variant) => {
      return variant.options.every(
        (option, index) => option === selectedOptions[index],
      );
    });
  }

  function updateProductMedia(media, source) {
    const image = media?.querySelector(
      '.md-pdp__media-main img, .md-product-pdp__main-image img',
    );
    if (!media || !image || !source?.src) return;

    image.src = source.src;
    if (source.srcset) image.srcset = source.srcset;
    if (source.alt) image.alt = source.alt;

    media.querySelectorAll(selectors.mediaThumb).forEach((button) => {
      const active = source.id
        ? button.dataset.mediaId === String(source.id)
        : button.dataset.mediaSrc === source.src;
      button.classList.toggle('md-pdp__thumb--active', active);
      button.classList.toggle('md-product-pdp__thumb--active', active);
      if (active) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }

  function setUnavailableProductVariant(picker) {
    const scope = getProductScope(picker);
    const mainForm = getMainProductForm(picker);
    const formScope = mainForm || scope;

    formScope.querySelectorAll('[data-product-variant-id]').forEach((input) => {
      input.value = '';
    });

    formScope.querySelectorAll('[data-selling-plan-input]').forEach((input) => {
      input.value = '';
    });

    formScope.querySelectorAll('[data-product-submit]').forEach((button) => {
      button.disabled = true;
      button.dataset.variantAvailable = 'false';
      button.textContent = button.dataset.soldOutLabel || 'Unavailable';
    });
  }

  function updateVariantUrl(picker, variant) {
    if (!variant?.id || !getProductRoot(picker)) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === String(variant.id)) return;
    url.searchParams.set('variant', variant.id);
    window.history.replaceState(window.history.state, '', url.toString());
  }

  function updateSubscriptionCard(label, input, allocation, oneTimePrice) {
    input.value = String(allocation.id);
    input.dataset.sellingPlanId = String(allocation.id);
    input.dataset.purchaseOptionPrice = allocation.price || '';

    const cardPrice = label.querySelector('[data-selling-plan-card-price]');
    if (cardPrice) cardPrice.textContent = allocation.price || '';

    const comparePrice = label.querySelector(
      '.md-product-pdp__purchase-option-price s',
    );
    if (comparePrice) comparePrice.textContent = oneTimePrice || '';

    const cardLabel = label.querySelector('[data-selling-plan-card-label]');
    if (cardLabel) {
      const discountPercent = Number(allocation.discountPercent || 0);
      const baseLabel = cardLabel.dataset.subscribeBase || '';
      const saveWord = cardLabel.dataset.saveWord || 'save';
      const labelJoiner = cardLabel.dataset.labelJoiner || ' & ';
      const percentSpacing = cardLabel.dataset.percentSpacing || '';
      cardLabel.textContent =
        discountPercent > 0
          ? `${baseLabel}${labelJoiner}${saveWord} ${discountPercent}${percentSpacing}%`
          : baseLabel;
    }
  }

  function rebuildSellingPlanSelect(select, allocations, selectedPlanId) {
    select.replaceChildren();
    allocations.forEach((allocation) => {
      const option = document.createElement('option');
      option.value = String(allocation.id);
      option.textContent = allocation.name || '';
      option.dataset.purchaseOptionPrice = allocation.price || '';
      option.dataset.discountPercent = String(allocation.discountPercent || 0);
      option.selected = String(allocation.id) === selectedPlanId;
      select.append(option);
    });
  }

  function updateRequiredPlanSubmission(scope, required, hasValidPlan) {
    scope.querySelectorAll('[data-product-submit]').forEach((button) => {
      const variantAvailable = button.dataset.variantAvailable === 'true';
      const purchaseOptionValid = !required || hasValidPlan;
      button.dataset.purchaseOptionValid = purchaseOptionValid
        ? 'true'
        : 'false';
      button.disabled = !variantAvailable || !purchaseOptionValid;
    });
  }

  function updateOneTimeCard(scope, variant) {
    const price = scope.querySelector('[data-one-time-card-price]');
    if (price) price.textContent = variant.price || '';

    const comparePrice = scope.querySelector(
      '[data-one-time-card-compare-price]',
    );
    if (comparePrice) {
      const comparePriceValue =
        variant.compareAtPrice && variant.compareAtPrice !== variant.price
          ? variant.compareAtPrice
          : '';
      comparePrice.hidden = !comparePriceValue;
      comparePrice.textContent = comparePriceValue;
    }
  }

  function clearSellingPlanUrl(scope) {
    if (!getProductRoot(scope)) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has('selling_plan')) return;
    url.searchParams.delete('selling_plan');
    window.history.replaceState(window.history.state, '', url.toString());
  }

  function updateSellingPlanAvailability(scope, variant) {
    const planInput = scope.querySelector('[data-selling-plan-input]');
    if (!planInput) return;

    const oneTimeInput = scope.querySelector(
      'input[name="purchase_option"][data-selling-plan-id=""]',
    );
    const required = !oneTimeInput;
    if (oneTimeInput && variant.price) {
      oneTimeInput.dataset.purchaseOptionPrice = variant.price;
    }

    const planOption = scope.querySelector('[data-selling-plan-option]');
    const subscriptionInput = planOption?.querySelector(
      'input[name="purchase_option"][data-selling-plan-id]',
    );
    const planSelect = planOption?.querySelector('[data-selling-plan-select]');
    if (!planOption || !subscriptionInput || !planSelect) {
      planInput.value = '';
      if (oneTimeInput) {
        oneTimeInput.checked = true;
        oneTimeInput.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        clearSellingPlanUrl(scope);
      }
      updateRequiredPlanSubmission(scope, required, false);
      return;
    }

    const allocations = variant.sellingPlanAllocations || [];
    const currentChecked = scope.querySelector(
      'input[name="purchase_option"]:checked',
    );
    const preserveOneTime = Boolean(
      oneTimeInput && (!currentChecked || currentChecked === oneTimeInput),
    );
    const previousPlanId = String(
      subscriptionInput.dataset.sellingPlanId || planInput.value || '',
    );
    const selectedAllocation =
      allocations.find(
        (allocation) => String(allocation.id) === previousPlanId,
      ) || allocations[0];
    const selectedPlanId = selectedAllocation
      ? String(selectedAllocation.id)
      : '';

    rebuildSellingPlanSelect(planSelect, allocations, selectedPlanId);
    planOption.hidden = !selectedAllocation;
    subscriptionInput.disabled = !selectedAllocation;
    planSelect.disabled = !selectedAllocation;

    if (selectedAllocation) {
      updateSubscriptionCard(
        planOption,
        subscriptionInput,
        selectedAllocation,
        variant.price,
      );
    } else {
      subscriptionInput.checked = false;
      subscriptionInput.value = '';
      subscriptionInput.dataset.sellingPlanId = '';
      subscriptionInput.dataset.purchaseOptionPrice = '';
    }

    if (preserveOneTime) {
      oneTimeInput.checked = true;
      oneTimeInput.dispatchEvent(new Event('change', { bubbles: true }));
      updateRequiredPlanSubmission(scope, false, true);
      return;
    }

    if (selectedAllocation) {
      subscriptionInput.checked = true;
      subscriptionInput.dispatchEvent(new Event('change', { bubbles: true }));
      updateRequiredPlanSubmission(scope, required, true);
      return;
    }

    if (oneTimeInput) {
      oneTimeInput.checked = true;
      oneTimeInput.dispatchEvent(new Event('change', { bubbles: true }));
      updateRequiredPlanSubmission(scope, false, true);
      return;
    }

    planInput.value = '';
    clearSellingPlanUrl(scope);
    updateRequiredPlanSubmission(scope, required, false);
  }

  function updateProductVariant(picker) {
    const variant = findSelectedVariant(picker);
    if (!variant) {
      setUnavailableProductVariant(picker);
      return;
    }

    const scope = getProductScope(picker);
    const mainForm = getMainProductForm(picker);
    const formScope = mainForm || scope;

    formScope.querySelectorAll('[data-product-variant-id]').forEach((input) => {
      input.value = variant.id;
    });

    const price = scope.querySelector('[data-product-price]');
    if (price) {
      price.textContent = variant.price;
      price.dataset.oneTimePrice = variant.price;
      price.dataset.oneTimeComparePrice = variant.compareAtPrice || '';
    }

    const compare = scope.querySelector('[data-product-compare]');
    if (compare) {
      if (variant.compareAtPrice && variant.compareAtPrice !== variant.price) {
        compare.hidden = false;
        compare.textContent = variant.compareAtPrice;
      } else {
        compare.hidden = true;
        compare.textContent = '';
      }
    }

    updateOneTimeCard(formScope, variant);

    formScope.querySelectorAll('[data-product-submit]').forEach((button) => {
      const addLabel = button.dataset.addLabel || '';
      const soldOutLabel = button.dataset.soldOutLabel || '';
      const currency = button.dataset.currency || '';
      button.dataset.variantAvailable = variant.available ? 'true' : 'false';
      button.disabled =
        !variant.available || button.dataset.purchaseOptionValid === 'false';
      button.innerHTML = variant.available
        ? `<span>${escapeHtml(addLabel)}</span><span aria-hidden="true">-</span><span data-product-submit-price>${escapeHtml(variant.price)}</span><span>${escapeHtml(currency)}</span>`
        : soldOutLabel;
    });

    if (variant.featuredMedia) {
      const media = getProductRoot(picker)?.querySelector(
        '.md-pdp__media, .md-product-pdp__gallery',
      );
      updateProductMedia(media, variant.featuredMedia);
    }

    updateSellingPlanAvailability(formScope, variant);
    updateVariantUrl(picker, variant);
  }

  async function loadProductRecommendations(section) {
    if (!section?.dataset.url || section.dataset.loaded === 'true') return true;

    const hasFallback = section.dataset.hasFallback === 'true';
    const currentState = recommendationLoadState.get(section);
    if (currentState?.loading) return false;

    const controller = new AbortController();
    recommendationLoadState.set(section, { controller, loading: true });
    section.dataset.loading = 'true';

    try {
      const response = await fetch(section.dataset.url, {
        headers: { Accept: 'text/html' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Recommendations request failed');

      const parsed = new DOMParser().parseFromString(
        await response.text(),
        'text/html',
      );
      const next = parsed.querySelector(selectors.productRecommendations);
      if (recommendationLoadState.get(section)?.controller !== controller) {
        return false;
      }

      section.dataset.loaded = 'true';
      delete section.dataset.retryAttempts;
      const retryTimer = recommendationRetryTimers.get(section);
      if (retryTimer) {
        window.clearTimeout(retryTimer);
        recommendationRetryTimers.delete(section);
      }

      if (next?.innerHTML.trim() && !next.hidden) {
        section.hidden = false;
        delete section.dataset.hasFallback;
        section.innerHTML = next.innerHTML;
        document.dispatchEvent(
          new CustomEvent('shopify:section-rendered', {
            detail: { sectionId: section.dataset.sectionId || '' },
          }),
        );
      } else if (!hasFallback) {
        section.hidden = true;
      }
      return true;
    } catch (error) {
      if (error.name !== 'AbortError') {
        if (!hasFallback) section.hidden = true;
        const retryAttempts = Number(section.dataset.retryAttempts || 0);
        if (retryAttempts < 2) {
          section.dataset.retryAttempts = String(retryAttempts + 1);
          const retryTimer = window.setTimeout(() => {
            recommendationRetryTimers.delete(section);
            if (section.isConnected && section.dataset.loaded !== 'true') {
              loadProductRecommendations(section);
            }
          }, 1200 * (retryAttempts + 1));
          recommendationRetryTimers.set(section, retryTimer);
        }
      }
      return false;
    } finally {
      if (recommendationLoadState.get(section)?.controller === controller) {
        recommendationLoadState.delete(section);
      }
      delete section.dataset.loading;
    }
  }

  function initProductRecommendations(root = document) {
    const sections = queryAllIncludingRoot(
      root,
      selectors.productRecommendations,
    );
    if (!sections.length) return;

    const hiddenSections = sections.filter((section) => section.hidden);
    hiddenSections.forEach(loadProductRecommendations);

    const visibleSections = sections.filter((section) => !section.hidden);
    if (!visibleSections.length) return;

    if (!('IntersectionObserver' in window)) {
      visibleSections.forEach(loadProductRecommendations);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadProductRecommendations(entry.target).then((loaded) => {
            if (loaded) observer.unobserve(entry.target);
          });
        });
      },
      { rootMargin: '0px 0px 260px 0px' },
    );

    visibleSections.forEach((section) => observer.observe(section));
  }

  function initVariantPickers(root = document) {
    queryAllIncludingRoot(root, selectors.variantPicker).forEach(
      updateProductVariant,
    );
  }

  function initDynamicContent(root = document) {
    initVariantPickers(root);
    initProductRecommendations(root);
  }

  function cleanupDynamicContent(root = document) {
    queryAllIncludingRoot(root, selectors.productRecommendations).forEach(
      (section) => {
        const retryTimer = recommendationRetryTimers.get(section);
        if (retryTimer) {
          window.clearTimeout(retryTimer);
          recommendationRetryTimers.delete(section);
        }

        recommendationLoadState.get(section)?.controller?.abort();
        recommendationLoadState.delete(section);
      },
    );
  }

  document.addEventListener('click', (event) => {
    const mediaThumb = event.target.closest(selectors.mediaThumb);
    if (mediaThumb) {
      event.preventDefault();
      updateProductMedia(getProductMediaRoot(mediaThumb), {
        id: mediaThumb.dataset.mediaId,
        src: mediaThumb.dataset.mediaSrc,
        srcset: mediaThumb.dataset.mediaSrcset,
        alt: mediaThumb.dataset.mediaAlt,
      });
      return;
    }
  });

  document.addEventListener('change', (event) => {
    const option = event.target.closest(selectors.variantOption);
    if (!option) return;
    const picker = option.closest(selectors.variantPicker);
    if (!picker) return;
    updateProductVariant(picker);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initDynamicContent(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    cleanupDynamicContent(event.target);
  });

  initDynamicContent();

  window.MedistikProductMedia = {
    update: updateProductMedia,
    updateVariant: updateProductVariant,
  };
})();
